import React from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import actionsHandler from "@/store/actions/actionHandler";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { GroupCreateResponse } from "@/pages/LiveChat/types/api-response-types";
interface LiveChatGroupCreateProps {
  onClose: () => void;
  onSuccess?: (group: GroupCreateResponse) => void;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Group name is required")
    .min(3, "Group name must be at least 3 characters")
    .max(50, "Group name must not exceed 50 characters"),
});

const LiveChatGroupCreate = ({
  onClose,
  onSuccess,
}: LiveChatGroupCreateProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();

  const handleSubmit = async (
    values: { name: string },
    { setSubmitting }: any,
  ) => {
    try {
      const { authenticatedCall } = actionsHandler("createGroupChat");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            name: values.name,
          },
        }),
      );

      if (response.ok) {
        toast.success("Group created successfully");
        onSuccess?.(response.data);
        onClose();
      } else {
        toast.error(response.data?.message || "Failed to create group");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Failed to create group");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-w-[400px] tw-p-6">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div></div>
          <h2 className="tw-text-[#232323] tw-text-base tw-font-semibold">
            Create Group
          </h2>
          <button onClick={onClose}>
            <img
              src={CloseIcon}
              alt="close"
              className="tw-h-4 tw-w-4 tw-min-w-4 tw-min-h-4"
            />
          </button>
        </div>

        <Formik
          initialValues={{ name: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form>
              <div className="tw-mb-6">
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Group Name
                </label>
                <TextField
                  fullWidth
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  size="small"
                  placeholder="Enter group name"
                />
                <span className="tw-text-red-500 tw-text-xs">
                  {touched.name && errors.name}
                </span>
              </div>

              <div className="tw-flex tw-space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="tw-flex-1 tw-px-4 tw-py-2 tw-border tw-text-[#414651] tw-border-[#D5D7DA] tw-rounded-lg"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="tw-flex-1 tw-bg-[#146C94] tw-text-white tw-rounded-lg tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-center tw-gap-2"
                >
                  {isSubmitting ? (
                    <CustomLoader loading={true} isWrapper={true} size="20" />
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LiveChatGroupCreate;
